import { Box, Container, Typography, Paper, List, ListItem, ListItemText, Divider, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
    const navigate = useNavigate();
    const lastUpdated = "April 14, 2026";
    const companyName = "Shia's Transportation";
    const appName = "Shia's Transportation";
    const contactEmail = "contact@shiastransport.com";
    const websiteUrl = "https://www.shiastransportation.com";

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
            <Container maxWidth="md">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/')}
                    sx={{ mb: 3 }}
                >
                    Back to Home
                </Button>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 5 } }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        Terms of Service
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Updated: {lastUpdated}
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    {/* Agreement to Terms */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        1. Agreement to Terms
                    </Typography>
                    <Typography paragraph>
                        By accessing or using {appName}'s services, including our mobile applications and web
                        platforms, you agree to be bound by these Terms of Service ("Terms"). If you do not
                        agree to these Terms, you may not access or use our services.
                    </Typography>
                    <Typography paragraph>
                        These Terms apply to all users of our services, including drivers, dispatchers, and
                        passengers who interact with our transportation platform.
                    </Typography>

                    {/* Description of Services */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        2. Description of Services
                    </Typography>
                    <Typography paragraph>
                        {companyName} provides a transportation dispatch platform that connects passengers with
                        drivers through our dispatcher network. Our services include:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemText primary="Mobile application for drivers to receive and manage ride assignments" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Web-based dashboard for dispatchers to coordinate rides" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Real-time communication between drivers and dispatchers" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Payment processing for transportation services" />
                        </ListItem>
                    </List>

                    {/* User Accounts */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        3. User Accounts
                    </Typography>
                    <Typography paragraph>
                        To use certain features of our services, you must create an account. You agree to:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemText primary="Provide accurate and complete information during registration" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Maintain the security of your account credentials" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Notify us immediately of any unauthorized access to your account" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Accept responsibility for all activities that occur under your account" />
                        </ListItem>
                    </List>
                    <Typography paragraph>
                        We reserve the right to suspend or terminate accounts that violate these Terms or
                        engage in fraudulent or illegal activities.
                    </Typography>

                    {/* Driver Requirements */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        4. Driver Requirements
                    </Typography>
                    <Typography paragraph>
                        Drivers using our platform must:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemText primary="Possess a valid driver's license appropriate for the vehicle operated" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Maintain valid vehicle registration and insurance" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Keep the vehicle in safe operating condition" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Comply with all applicable local, state, and federal laws" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Enable location services while actively accepting rides" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Provide professional and courteous service to passengers" />
                        </ListItem>
                    </List>

                    {/* Acceptable Use */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        5. Acceptable Use
                    </Typography>
                    <Typography paragraph>
                        You agree not to:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemText primary="Use our services for any unlawful purpose" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Interfere with or disrupt the operation of our services" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Attempt to gain unauthorized access to our systems" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Impersonate another person or entity" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Share your account credentials with others" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Use automated systems to access our services without permission" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Harass, threaten, or discriminate against other users" />
                        </ListItem>
                    </List>

                    {/* Payments and Fees */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        6. Payments and Fees
                    </Typography>
                    <Typography paragraph>
                        Payment for transportation services is processed through Square, Inc. By using our
                        payment services, you agree to Square's terms of service. Fare amounts are determined
                        based on factors including distance, time, and vehicle type.
                    </Typography>
                    <Typography paragraph>
                        Drivers receive compensation for completed rides according to the payment terms
                        established with {companyName}. We reserve the right to modify payment structures
                        with reasonable notice.
                    </Typography>
                    <Typography paragraph>
                        Cancellation fees may apply for rides cancelled after a driver has been assigned,
                        as determined by our cancellation policy.
                    </Typography>

                    {/* Intellectual Property */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        7. Intellectual Property
                    </Typography>
                    <Typography paragraph>
                        All content, features, and functionality of our services, including but not limited
                        to text, graphics, logos, and software, are owned by {companyName} and are protected
                        by copyright, trademark, and other intellectual property laws.
                    </Typography>
                    <Typography paragraph>
                        You are granted a limited, non-exclusive, non-transferable license to use our
                        applications for their intended purpose. You may not copy, modify, distribute, or
                        reverse engineer any part of our services.
                    </Typography>

                    {/* Disclaimers */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        8. Disclaimers
                    </Typography>
                    <Typography paragraph>
                        OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                        EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT OUR SERVICES WILL BE UNINTERRUPTED,
                        ERROR-FREE, OR COMPLETELY SECURE.
                    </Typography>
                    <Typography paragraph>
                        {companyName} is a technology platform that connects drivers with passengers through
                        dispatchers. We are not a transportation carrier and do not provide transportation
                        services directly. Drivers are independent contractors, not employees of {companyName}.
                    </Typography>

                    {/* Limitation of Liability */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        9. Limitation of Liability
                    </Typography>
                    <Typography paragraph>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE LIABLE
                        FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                        BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemText primary="Your use or inability to use our services" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Any conduct or content of third parties on our services" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Unauthorized access to or alteration of your data" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="Any transportation services provided by drivers" />
                        </ListItem>
                    </List>

                    {/* Indemnification */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        10. Indemnification
                    </Typography>
                    <Typography paragraph>
                        You agree to indemnify and hold harmless {companyName}, its officers, directors,
                        employees, and agents from any claims, damages, losses, liabilities, and expenses
                        (including attorney's fees) arising out of your use of our services, your violation
                        of these Terms, or your violation of any rights of another party.
                    </Typography>

                    {/* Termination */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        11. Termination
                    </Typography>
                    <Typography paragraph>
                        We may terminate or suspend your account and access to our services at any time,
                        with or without cause or notice. Upon termination, your right to use our services
                        will immediately cease.
                    </Typography>
                    <Typography paragraph>
                        You may terminate your account at any time by contacting us. Termination does not
                        relieve you of any obligations incurred prior to termination.
                    </Typography>

                    {/* Governing Law */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        12. Governing Law
                    </Typography>
                    <Typography paragraph>
                        These Terms shall be governed by and construed in accordance with the laws of the
                        State of New York, without regard to its conflict of law provisions. Any disputes
                        arising from these Terms shall be resolved in the courts located in New York.
                    </Typography>

                    {/* Changes to Terms */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        13. Changes to Terms
                    </Typography>
                    <Typography paragraph>
                        We reserve the right to modify these Terms at any time. We will notify users of
                        material changes by posting the updated Terms on our website and updating the
                        "Last Updated" date. Your continued use of our services after such changes
                        constitutes acceptance of the new Terms.
                    </Typography>

                    {/* Severability */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        14. Severability
                    </Typography>
                    <Typography paragraph>
                        If any provision of these Terms is found to be unenforceable or invalid, that
                        provision shall be limited or eliminated to the minimum extent necessary, and the
                        remaining provisions shall remain in full force and effect.
                    </Typography>

                    {/* Service & Policy Details */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        15. Service Overview
                    </Typography>
                    <Typography paragraph>
                        Shia's Transportation LLC provides professional transportation services throughout the Tri‑State Area. By booking a trip, visiting our website, or using our services, you agree to all policies, terms, and conditions outlined in this document.
                    </Typography>
                    <Typography paragraph>
                        All bookings are subject to driver availability, vehicle availability, weather and road conditions, and operational constraints. We reserve the right to refuse or cancel service at our discretion.
                    </Typography>

                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        16. Customer Responsibilities
                    </Typography>
                    <Typography paragraph>
                        Customers must:
                    </Typography>
                    <List dense>
                        <ListItem><ListItemText primary="Provide accurate pickup and drop‑off information" /></ListItem>
                        <ListItem><ListItemText primary="Be reachable by phone" /></ListItem>
                        <ListItem><ListItemText primary="Be ready at the scheduled pickup time" /></ListItem>
                        <ListItem><ListItemText primary="Ensure all passengers behave respectfully" /></ListItem>
                        <ListItem><ListItemText primary="Check the vehicle for personal belongings before exiting" /></ListItem>
                    </List>
                    <Typography paragraph>
                        Failure to comply may result in fees or refusal of service.
                    </Typography>

                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        17. Payment Authorization
                    </Typography>
                    <Typography paragraph>
                        By booking with Shia's Transportation LLC, you authorize us to charge:
                    </Typography>
                    <List dense>
                        <ListItem><ListItemText primary="Trip fare" /></ListItem>
                        <ListItem><ListItemText primary="Wait‑time fees" /></ListItem>
                        <ListItem><ListItemText primary="Tolls" /></ListItem>
                        <ListItem><ListItemText primary="Cleaning fees" /></ListItem>
                        <ListItem><ListItemText primary="Cancellation fees" /></ListItem>
                        <ListItem><ListItemText primary="No‑show fees" /></ListItem>
                        <ListItem><ListItemText primary="Damage fees" /></ListItem>
                    </List>
                    <Typography paragraph>
                        Charges may be applied to the payment method on file.
                    </Typography>

                    {/* Cancellation & No-Show Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        18. Cancellation & No‑Show Policy
                    </Typography>
                    <Typography paragraph>
                        <strong>Free Cancellation Window:</strong> Customers may cancel within 20 minutes of booking for free as long as the driver has NOT begun heading to the pickup location. This applies even if the trip is less than 2 hours away.
                    </Typography>
                    <Typography paragraph>
                        <strong>2+ Hours Before Pickup (Driver NOT En Route):</strong> Free cancellation — no fees of any kind.
                    </Typography>
                    <Typography paragraph>
                        <strong>Under 2 Hours (Driver NOT En Route):</strong> $50 cancellation fee.
                    </Typography>
                    <Typography paragraph>
                        <strong>Under 1 Hour (Driver NOT En Route):</strong> Customer is charged the cheaper of $75 or the full fare.
                    </Typography>
                    <Typography paragraph>
                        <strong>Driver En Route (Any Time):</strong> If the driver has started heading to the pickup location, 50% of the fare is charged. This overrides all other rules — the 20‑minute free cancellation window no longer applies. This applies even if the driver left up to 3 hours before pickup.
                    </Typography>
                    <Typography paragraph>
                        <strong>Driver Already on Location & Pickup &lt; 30 Minutes Away:</strong> If the driver is already at the pickup location and the scheduled pickup time is less than 30 minutes away, the customer is charged the full fare.
                    </Typography>
                    <Typography paragraph>
                        <strong>No‑Show:</strong> A no‑show occurs when the driver arrives, free wait‑time expires, and the customer cannot be reached. Charge: full fare + all applicable wait‑time charges.
                    </Typography>

                    {/* Wait-Time Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        19. Wait‑Time Policy
                    </Typography>
                    <Typography paragraph>
                        All vehicle types include 7 minutes of free wait time for standard (non‑airport) trips. After free time, the following per‑minute rates apply:
                    </Typography>
                    <List dense>
                        <ListItem><ListItemText primary="Sedans & Minivans: $0.50/min" /></ListItem>
                        <ListItem><ListItemText primary="Luxury SUVs: $1.00/min" /></ListItem>
                        <ListItem><ListItemText primary="15‑Passenger Vans: $1.25/min" /></ListItem>
                        <ListItem><ListItemText primary="Mercedes Sprinters: $1.50/min" /></ListItem>
                    </List>

                    {/* Airport Pickup Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        20. Airport Pickup Policy
                    </Typography>
                    <Typography paragraph>
                        Free airport wait time:
                    </Typography>
                    <List dense>
                        <ListItem><ListItemText primary="Domestic flights: 40 minutes free after landing" /></ListItem>
                        <ListItem><ListItemText primary="International flights: 50 minutes free after landing" /></ListItem>
                    </List>
                    <Typography paragraph>
                        After free time, the vehicle‑specific per‑minute rate applies. We track flights, but customers must notify us of gate changes, airline changes, or missed connections. Failure to notify may result in wait‑time charges or no‑show fees.
                    </Typography>

                    {/* Cleaning & Damage Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        21. Cleaning & Damage Policy
                    </Typography>
                    <Typography paragraph>
                        If a passenger vomits or creates a significant mess requiring professional cleaning, a $100 cleaning fee will be charged. This includes vomit, spilled food or drinks, bodily fluids, excessive dirt or debris, and any mess requiring deodorizing or sanitizing.
                    </Typography>
                    <Typography paragraph>
                        Customers are responsible for full repair costs and vehicle downtime if the vehicle must be removed from service. Examples include broken seats or panels, stains that cannot be cleaned, damage to electronics, and scratches or dents.
                    </Typography>

                    {/* Lost & Found Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        22. Lost & Found Policy
                    </Typography>
                    <Typography paragraph>
                        Shia's Transportation LLC is not responsible for lost, stolen, damaged, or left‑behind items. Passengers must check the vehicle before exiting.
                    </Typography>
                    <Typography paragraph>
                        If a driver must return an item, a delivery fee may apply, return is not guaranteed, and items may need to be picked up at a designated location. Items not claimed within a reasonable time may be discarded, donated, or disposed of at our discretion.
                    </Typography>

                    {/* Liability Disclaimer */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        23. Liability Disclaimer
                    </Typography>
                    <Typography paragraph>
                        Shia's Transportation LLC is not liable for:
                    </Typography>
                    <List dense>
                        <ListItem><ListItemText primary="Missed flights" /></ListItem>
                        <ListItem><ListItemText primary="Missed appointments" /></ListItem>
                        <ListItem><ListItemText primary="Delays caused by traffic, weather, or road conditions" /></ListItem>
                        <ListItem><ListItemText primary="Indirect or consequential damages" /></ListItem>
                        <ListItem><ListItemText primary="Business losses" /></ListItem>
                        <ListItem><ListItemText primary="Emotional distress" /></ListItem>
                    </List>
                    <Typography paragraph sx={{ fontWeight: 'bold' }}>
                        Maximum liability is limited to the fare paid.
                    </Typography>

                    {/* Safety & Conduct Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        24. Safety & Conduct Policy
                    </Typography>
                    <Typography paragraph>
                        We reserve the right to refuse service to any passenger who acts aggressively, appears intoxicated, poses a safety risk, damages property, or violates company rules. Drivers may end a trip early if safety is compromised.
                    </Typography>

                    {/* Route Selection Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        25. Route Selection Policy
                    </Typography>
                    <Typography paragraph>
                        Drivers may choose alternate routes based on traffic, safety, GPS updates, or road closures. Customer route requests may be honored when safe and reasonable.
                    </Typography>

                    {/* Weather & Emergency Policy */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        26. Weather & Emergency Policy
                    </Typography>
                    <Typography paragraph>
                        Service may be delayed or canceled due to severe weather, natural disasters, road closures, or state/federal emergencies. Safety is always the priority.
                    </Typography>

                    {/* Standard Disclaimers */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        27. Standard Disclaimers
                    </Typography>
                    <List dense>
                        <ListItem><ListItemText primary="All travel times are estimates only" /></ListItem>
                        <ListItem><ListItemText primary="We are not responsible for third‑party systems (airlines, apps, payment processors)" /></ListItem>
                        <ListItem><ListItemText primary="Policies may be updated at any time" /></ListItem>
                        <ListItem><ListItemText primary="Continued use of our services constitutes acceptance of updated terms" /></ListItem>
                    </List>

                    {/* Contact Information */}
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
                        28. Contact Information
                    </Typography>
                    <Typography paragraph>
                        If you have any questions about these Terms, please contact us:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemText primary={`Email: ${contactEmail}`} />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary={`Website: ${websiteUrl}`} />
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography variant="body2" color="text.secondary" align="center">
                        © 2024 {companyName}. All rights reserved.
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default TermsOfService;
